
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import AppContactSearch from "@/components/contacts/ContactSearch.vue";
import AppLedgerAccountAutocomplete from "@/components/common/LedgerAccountAutocomplete.vue";
import AppCustomTable from "@/components/common/CustomTable.vue";
import AppSelectJournal from "../SelectJournal.vue";
import { FixedAsset, FIXED_ASSET_METHODS, FIXED_ASSET_PRO_RATA_MODES, IDepreciationPeriod } from "@/models/base/fixed-asset";
import { FixedAssetService, IInvestmentPurchase } from "@/services/fixed-asset-service";
import { BookYearService } from "@/services";
import { LedgerAccountService } from "@/services/ledger-account-service";
import { LedgerAccount } from "@/models/ledger-account";
import { Contact } from "@/models/base";
import { PurchaseLedger } from "@/models/base/purchase-ledger";
import { printHumanFloat, printLocalDate } from "@/utils/pretty-print";

interface IWizardStep {
	key:string;
	title:string;
	hint:string;
}

export default Vue.extend({
	data() {
		return {
			asset: new FixedAsset(),
			contact: null as Contact | null,
			purchases: [] as IInvestmentPurchase[],
			selectedPurchaseIndex: -1,
			ledgerAccounts: [] as LedgerAccount[],
			schedule: [] as IDepreciationPeriod[],
			bookNow: true,
			isSaving: false,
			isDisposed: false,
			previewTimer: 0,
			ready: false,
			step: 0
		};
	},
	computed: {
		bookYear():number {
			return BookYearService.selectedBookYear;
		},
		steps():IWizardStep[] {
			return ["purchase", "values", "ledgers", "review"].map(key => ({
				key,
				title: this.$ct(`wizard.step-${key}`).toString(),
				hint: this.$ct(`wizard.step-${key}-hint`).toString()
			}));
		},
		lastStep():number {
			return this.steps.length - 1;
		},
		firstPeriod():IDepreciationPeriod | null {
			return this.schedule.length ? this.schedule[0] : null;
		},
		lastPeriod():IDepreciationPeriod | null {
			return this.schedule.length ? this.schedule[this.schedule.length - 1] : null;
		},
		methodItems():ISelectItem<string>[] {
			return FIXED_ASSET_METHODS.map(m => ({text: this.$ct(`methods.${m}`).toString(), value: m}));
		},
		proRataItems():ISelectItem<string>[] {
			return FIXED_ASSET_PRO_RATA_MODES.map(m => ({text: this.$ct(`pro-rata-modes.${m}`).toString(), value: m}));
		},
		bookYearItems():ISelectItem<number>[] {
			let items:ISelectItem<number>[] = [{text: this.$ct("first-bookyear-from-start").toString(), value: 0}];
			for (let by of BookYearService.bookYears) {
				items.push({text: `${by.Year}`, value: by.Year});
			}
			return items;
		},
		purchaseItems():ISelectItem<number>[] {
			let items:ISelectItem<number>[] = [{text: this.$ct("no-purchase").toString(), value: -1}];
			this.purchases.forEach((item, index) => {
				let p = item.purchase;
				let name = p.Contact ? p.Contact.getDisplayName() : "";
				let suffix = item.fixedAssetID && item.fixedAssetID != this.asset.ID ? ` (${this.$ct("already-linked")})` : "";
				items.push({text: `${p.ComputedFriendlyID} - ${name} - ${printLocalDate(p.InvoiceDate)} - ${printHumanFloat(p.VATBuisinessAssets)}${suffix}`, value: index});
			});
			return items;
		},
		hasPurchase():boolean {
			return this.selectedPurchaseIndex >= 0 && this.selectedPurchaseIndex < this.purchases.length;
		},
		purchaseInvestmentLedgers():PurchaseLedger[] {
			if (!this.hasPurchase) return [];
			return this.purchases[this.selectedPurchaseIndex].purchase.Ledgers.filter(l => /^2[0-8]/.test(l.LedgerCode));
		},
		investmentLedgerItems():ISelectItem<string>[] {
			return this.purchaseInvestmentLedgers.map(l => {
				let description = l.LedgerAccount ? l.LedgerAccount.Description : (this.findLedger(l.LedgerCode) || {Description: ""}).Description;
				return {text: `${l.LedgerCode} - ${description} - ${printHumanFloat(l.Amount)}`, value: l.LedgerCode};
			});
		},
		disposedAt: {
			get():Date {
				return this.asset.DisposedAt || new Date();
			},
			set(value:Date) {
				this.asset.DisposedAt = value;
			}
		},
		ledgersValid():boolean {
			if (this.asset.Years <= 0) return true;
			return !!this.asset.DepreciationLedgerCode && !!this.asset.CostLedgerCode;
		},
		canSave():boolean {
			return !!this.asset.Name && this.ledgersValid;
		}
	},
	watch: {
		asset: {
			deep: true,
			handler() {
				this.schedulePreview();
			}
		},
		isDisposed(value:boolean) {
			if (!value) {
				this.asset.DisposedAt = null;
			} else if (!this.asset.DisposedAt) {
				this.asset.DisposedAt = new Date();
			}
		},
		contact(value:Contact | null) {
			this.asset.ContactID = value ? value.ID : 0;
		},
		selectedPurchaseIndex(index:number) {
			this.onPurchaseSelect(index);
		}
	},
	methods: {
		printHumanFloat,
		printLocalDate,
		stepValid(index:number):boolean {
			if (index == 0) return !!this.asset.Name;
			if (index == 2) return this.ledgersValid;
			return true;
		},
		periodStatus(period:IDepreciationPeriod):string {
			if (period.Historic) return this.$ct("status-historic").toString();
			if (period.Booked) return this.$ct("status-booked").toString();
			return this.$ct("status-planned").toString();
		},
		periodClass(period:IDepreciationPeriod):string {
			if (period.Historic) return "fa-chip-neutral";
			if (period.Booked) return "fa-chip-active";
			return "fa-chip-planned";
		},
		schedulePreview() {
			if (!this.ready) return;
			clearTimeout(this.previewTimer);
			this.previewTimer = setTimeout(async () => {
				this.schedule = await FixedAssetService.previewSchedule(this.asset);
			}, 400) as any;
		},
		findLedger(code:string):LedgerAccount | undefined {
			return this.ledgerAccounts.find(l => l.Code == code);
		},
		onAssetLedgerInput(code:string) {
			if (!code || code.length < 6) return;
			let depreciationCode = code.substring(0, 3) + "900";
			if (!this.asset.DepreciationLedgerCode && this.findLedger(depreciationCode)) {
				this.asset.DepreciationLedgerCode = depreciationCode;
			}
			if (this.asset.CostLedgerCode) return;
			let prefix = code.substring(0, 2);
			let candidates = prefix == "20" ? ["630000"] : prefix == "21" ? ["630100", "630200"] : ["630200", "630000"];
			for (let candidate of candidates) {
				if (this.findLedger(candidate)) {
					this.asset.CostLedgerCode = candidate;
					return;
				}
			}
		},
		onInvestmentLedgerSelect(code:string) {
			this.asset.AssetLedgerCode = code;
			this.onAssetLedgerInput(code);
			if (this.asset.ID) return;
			let ledger = this.purchaseInvestmentLedgers.find(l => l.LedgerCode == code);
			let p = this.purchases[this.selectedPurchaseIndex].purchase;
			if (ledger) {
				this.asset.AcquisitionValue = Math.round((ledger.Amount + p.VATNotSubtractable) * 100) / 100;
			}
		},
		syncAssetLedgerWithPurchase() {
			let ledgers = this.purchaseInvestmentLedgers;
			if (ledgers.length == 0) return;
			if (ledgers.some(l => l.LedgerCode == this.asset.AssetLedgerCode)) return;
			this.onInvestmentLedgerSelect(ledgers[0].LedgerCode);
		},
		onPurchaseSelect(index:number) {
			if (index < 0) {
				this.asset.PurchaseID = 0;
				this.asset.PurchaseBookYear = 0;
				return;
			}
			let p = this.purchases[index].purchase;
			this.asset.PurchaseID = p.ID;
			this.asset.PurchaseBookYear = p.BookYear;
			if (p.Contact && p.Contact.ID) {
				this.contact = p.Contact;
			}
			if (this.asset.ID) {
				this.syncAssetLedgerWithPurchase();
				return;
			}
			this.asset.StartDate = new Date(p.InvoiceDate);
			this.asset.AcquisitionValue = Math.round((p.VATBuisinessAssets + p.VATNotSubtractable) * 100) / 100;
			if (!this.asset.Name) {
				this.asset.Name = p.Contact ? `${p.Contact.getDisplayName()} ${p.ComputedFriendlyID}` : p.ComputedFriendlyID;
			}
			this.asset.AssetLedgerCode = "";
			this.syncAssetLedgerWithPurchase();
		},
		cancel() {
			this.$emit("close");
		},
		async save() {
			this.isSaving = true;
			try {
				let result = await FixedAssetService.save(this.asset, this.bookNow);
				this.asset = result.asset;
				if (result.bookResult) {
					this.$wf.notify(this.$ct("book-result", result.bookResult).toString(), "success", 5000, "check");
				} else {
					this.$wf.notify(this.$ct("common.saved").toString(), "success", 3000, "check");
				}
				this.$emit("refresh");
			} finally {
				this.isSaving = false;
			}
		},
		async remove() {
			let confirm = await this.$confirmBuilder(this.$ct("delete-warning")).setWarningTitle().show();
			if (!confirm) return;
			this.isSaving = true;
			try {
				await FixedAssetService.delete(this.asset.ID);
				this.$emit("refresh");
			} finally {
				this.isSaving = false;
			}
		},
		selectPurchaseFromQuery() {
			let purchaseId = parseInt(`${this.$route.query.purchaseId || 0}`);
			let bookYear = parseInt(`${this.$route.query.bookYear || 0}`);
			if (!purchaseId) return;
			this.selectedPurchaseIndex = this.purchases.findIndex(p => p.purchase.ID == purchaseId && p.purchase.BookYear == bookYear);
		}
	},
	// eslint-disable-next-line max-lines-per-function
	async mounted() {
		this.ledgerAccounts = await LedgerAccountService.fetchLedgerAccounts();
		this.purchases = await FixedAssetService.getInvestmentPurchases(false);
		let id = parseInt(this.$route.params.id);
		if (id) {
			this.asset = await FixedAssetService.get(id);
			this.contact = this.asset.Contact;
			this.isDisposed = this.asset.DisposedAt != null;
			this.selectedPurchaseIndex = this.purchases.findIndex(p => p.purchase.ID == this.asset.PurchaseID && p.purchase.BookYear == this.asset.PurchaseBookYear);
			this.schedule = await FixedAssetService.getSchedule(id);
			this.step = this.lastStep;
		} else {
			this.selectPurchaseFromQuery();
		}
		this.$nextTick(() => {
			this.ready = true;
			if (!id) {
				this.schedulePreview();
			}
		});
	},
	components: {
		AppContactSearch,
		AppLedgerAccountAutocomplete,
		AppCustomTable,
		AppSelectJournal
	},
	i18nEx: {
		componentPrefix: "components.fixed-assets"
	}
});
